/**
 * Chat session orchestration hook
 * Follows ADR-002: Modularity - Composes smaller focused hooks
 * Follows ADR-003: High Cohesion - Single responsibility: orchestrate session lifecycle
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { log, logError } from '../utils/logger';
import { categorizeError, ErrorCategory } from './useErrorCategorization';
import { useRetryStrategy, DEFAULT_MAX_RETRY_DURATION } from './useRetryStrategy';
import { useCustomerResolution } from './useCustomerResolution';
import { useSessionCreation } from './useSessionCreation';
import type { EventCreationParams } from 'parlant-client/src/api';

// Re-export for backward compatibility
export { ErrorCategory } from './useErrorCategorization';
export { DEFAULT_MAX_RETRY_DURATION } from './useRetryStrategy';

/**
 * Options for useChatSession hook
 */
export interface UseChatSessionOptions {
  /** Server URL */
  serverUrl: string;
  /** Agent ID */
  agentId: string;
  /** Customer ID */
  customerId: string;
  /** Customer name (optional - falls back to customerId if not provided) */
  customerName?: string;
  /** Authentication token */
  authToken?: string;
  /** When true, session creation waits until authToken is present */
  requireAuth?: boolean;
  /** External session ID (if loading existing session) */
  externalSessionId?: string | null;
  /** Callback when session is created */
  onSessionCreated?: (sessionId: string) => void;
  /** Maximum time to retry connections before showing manual retry (default: 30000ms) */
  maxRetryDuration?: number;
}

/**
 * Return value for useChatSession hook
 */
export interface UseChatSessionReturn {
  /** Current session ID */
  sessionId: string | null;
  /** Whether session is being created */
  isCreatingSession: boolean;
  /** Error message if any */
  error: string;
  /** Whether connection has timed out and needs manual retry */
  connectionTimedOut: boolean;
  /** Whether there is a permanent error preventing session creation */
  hasPermanentError: boolean;
  /** Create a new session */
  createSession: (message?: EventCreationParams) => Promise<void>;
  /** Retry connection after timeout */
  retryConnection: () => void;
  /** Set error message */
  setError: (error: string) => void;
  /** Clear the session ID */
  clearSession: () => void;
}

/**
 * Custom hook for managing chat session creation and lifecycle
 * 
 * @example
 * ```tsx
 * const { sessionId, createSession, isCreatingSession } = useChatSession({
 *   serverUrl: 'https://api.example.com',
 *   agentId: 'agent-123',
 *   customerId: 'customer-456',
 *   onSessionCreated: (id) => console.log('Session:', id)
 * });
 * ```
 */
export const useChatSession = ({
  serverUrl,
  agentId,
  customerId,
  customerName,
  authToken,
  requireAuth = false,
  externalSessionId,
  onSessionCreated,
  maxRetryDuration = DEFAULT_MAX_RETRY_DURATION
}: UseChatSessionOptions): UseChatSessionReturn => {
  // State
  const [sessionId, setSessionId] = useState<string | null>(externalSessionId || null);
  const [isCreatingSession, setIsCreatingSession] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [connectionTimedOut, setConnectionTimedOut] = useState<boolean>(false);
  const [hasPermanentError, setHasPermanentError] = useState<boolean>(false);

  // Refs
  const sessionCreatedRef = useRef<boolean>(false);
  const permanentErrorRef = useRef<boolean>(false);
  const requestInFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(false);
  const errorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Composed hooks
  const retryStrategy = useRetryStrategy({
    maxRetryDuration,
    onTimeout: () => {
      setConnectionTimedOut(true);
      setError('Unable to connect to server. Please check your connection and try again.');
    }
  });

  const { resolveCustomerId } = useCustomerResolution({
    serverUrl,
    authToken
  });

  const { createSession: createSessionApi } = useSessionCreation({
    serverUrl,
    agentId,
    authToken
  });

  // Track mount state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (errorDebounceRef.current) {
        clearTimeout(errorDebounceRef.current);
      }
    };
  }, []);

  // Handle external sessionId changes
  useEffect(() => {
    if (externalSessionId && externalSessionId !== sessionId) {
      log('Loading existing session:', externalSessionId);
      setSessionId(externalSessionId);
      sessionCreatedRef.current = true;
      setConnectionTimedOut(false);
      retryStrategy.reset();
    }
  }, [externalSessionId, sessionId, retryStrategy]);

  /**
   * Creates a new session
   */
  const createSession = useCallback(
    async (message?: EventCreationParams): Promise<void> => {
      // Guard against concurrent requests
      if (isCreatingSession || requestInFlightRef.current) {
        log('Skipping createSession - request already in flight');
        return;
      }
      
      requestInFlightRef.current = true;

      try {
        setIsCreatingSession(true);
        setError('');

        // Resolve customer ID
        const resolvedCustomerId = customerId 
          ? await resolveCustomerId(customerId, customerName)
          : undefined;

        // Create session
        const session = await createSessionApi(resolvedCustomerId, message);

        setSessionId(session.id);
        onSessionCreated?.(session.id);

        setIsCreatingSession(false);
        requestInFlightRef.current = false;
        retryStrategy.reset();
        setConnectionTimedOut(false);
        setHasPermanentError(false);
      } catch (err) {
        const errorMessage = `Failed to create session: ${err instanceof Error ? err.message : 'Unknown error'}`;
        logError(errorMessage, err);
        
        const { category, statusCode } = categorizeError(err);
        
        if (category === ErrorCategory.PERMANENT) {
          permanentErrorRef.current = true;
          setHasPermanentError(true);
          log(`Permanent error detected (${statusCode || 'unknown'}) - stopping retries`);
        } else if (category === ErrorCategory.RATE_LIMITED) {
          log(`Rate limited (429) - will retry with longer delays`);
          setHasPermanentError(false);
        } else {
          log(`Transient error detected (${statusCode || 'unknown'}) - will retry`);
          setHasPermanentError(false);
        }
        
        // Debounce error state updates
        if (errorDebounceRef.current) {
          clearTimeout(errorDebounceRef.current);
        }
        errorDebounceRef.current = setTimeout(() => {
          if (mountedRef.current) {
            setError(errorMessage);
          }
        }, 100);
        
        setIsCreatingSession(false);
        requestInFlightRef.current = false;
        throw err;
      }
    },
    [customerId, customerName, resolveCustomerId, createSessionApi, onSessionCreated, isCreatingSession, retryStrategy]
  );

  /**
   * Resets retry state and attempts to connect again
   */
  const retryConnection = useCallback(() => {
    log('Manual retry triggered');
    setConnectionTimedOut(false);
    sessionCreatedRef.current = false;
    permanentErrorRef.current = false;
    requestInFlightRef.current = false;
    setHasPermanentError(false);
    setError('');
    retryStrategy.reset();
    setSessionId(null);
    
    createSession().catch((err) => {
      logError('Failed to create session on retry:', err);
    });
  }, [createSession, retryStrategy]);

  /**
   * Clears the session ID
   */
  const clearSession = useCallback(() => {
    log('Clearing session ID');
    setSessionId(null);
    sessionCreatedRef.current = false;
    permanentErrorRef.current = false;
    requestInFlightRef.current = false;
    setHasPermanentError(false);
    setConnectionTimedOut(false);
    retryStrategy.reset();
  }, [retryStrategy]);

  // Auto-create session on mount (and when auth becomes available)
  useEffect(() => {
    if (requireAuth && !authToken) {
      log('Skipping auto-create: authentication required but no token');
      return;
    }

    if (connectionTimedOut || permanentErrorRef.current) {
      log('Skipping auto-create: connectionTimedOut or permanentError');
      return;
    }

    if (requestInFlightRef.current) {
      log('Skipping auto-create: request already in flight');
      return;
    }

    if (!sessionId && !isCreatingSession && !sessionCreatedRef.current && !externalSessionId) {
      retryStrategy.markRetryStart();

      if (retryStrategy.shouldStopRetrying()) {
        return;
      }

      sessionCreatedRef.current = true;
      createSession().catch((err) => {
        if (!mountedRef.current) return;
        
        logError('Failed to create session on mount:', err);
        // NOTE: Don't reset sessionCreatedRef here - wait for scheduled retry
        // This prevents the effect from triggering immediately
        
        const { category } = categorizeError(err);
        
        if (category === ErrorCategory.PERMANENT) {
          permanentErrorRef.current = true;
          setHasPermanentError(true);
          sessionCreatedRef.current = false; // Only reset on permanent error (no retry)
          return;
        }
        
        setHasPermanentError(false);
        const delay = retryStrategy.calculateRetryDelay(category);
        log(`Scheduling retry in ${delay}ms`);
        
        retryStrategy.scheduleRetry(() => {
          if (!mountedRef.current) return;
          // Only reset after delay to allow retry
          sessionCreatedRef.current = false;
          // Force re-render to trigger effect
          setError('');
        }, delay);
      });
    }
  }, [externalSessionId, sessionId, isCreatingSession, createSession, connectionTimedOut, retryStrategy, requireAuth, authToken]);

  return {
    sessionId,
    isCreatingSession,
    error,
    connectionTimedOut,
    hasPermanentError,
    createSession,
    retryConnection,
    setError,
    clearSession
  };
};
